import { Component, computed, inject, OnInit, signal, Type, WritableSignal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { CalendarService } from '@lb/api/mylb/services';
import { ICalendarTokenStatusResponse } from '@lb/api/mylb/interfaces/icalendar-token-status-response.interface';
import { MDDialogService, MDIconComponent } from '@melodic-ui/components';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { tap } from 'rxjs';
import { AccountStateService } from '@lb/state/account-state.service';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';

@Component({
	selector: 'lb-calendar-quick-sync-button',
	standalone: true,
	imports: [CommonModule, DatePipe, MDIconComponent],
	templateUrl: './calendar-quick-sync-button.component.html',
	styleUrl: './calendar-quick-sync-button.component.scss'
})
export class CalendarQuickSyncButtonComponent implements OnInit {
	private _calendarService = inject(CalendarService);
	private _dialogService = inject(MDDialogService);
	private _accountStateService = inject(AccountStateService);
	private _appConfig: IAppSettings = inject(APP_CONFIG);

	public calendarTokenStatus: WritableSignal<ICalendarTokenStatusResponse | null> = signal(null);
	public loading: WritableSignal<boolean> = signal(false);
	public enableCalendarSync: boolean = this._appConfig.features?.enableCalendarSync ?? false;

	public isLanguageBirdUser = computed(() => {
		const user = this._accountStateService.user();
		return user?.username?.endsWith('@languagebird.com') ?? false;
	});

	public isSynced = computed(() => this.calendarTokenStatus()?.isConfigured ?? false);

	ngOnInit(): void {
		if (this.enableCalendarSync && this.isLanguageBirdUser()) {
			this.loadCalendarTokenStatus();
		}
	}

	loadCalendarTokenStatus(): void {
		this.loading.set(true);
		this._calendarService.getTokenStatus().subscribe({
			next: (status) => {
				this.calendarTokenStatus.set(status);
				this.loading.set(false);
			},
			error: () => {
				this.calendarTokenStatus.set(null);
				this.loading.set(false);
			}
		});
	}

	syncCalendar(): void {
		this.loading.set(true);
		this._calendarService.generateToken().subscribe({
			next: (status) => {
				this.calendarTokenStatus.set(status);
				if (status.url) {
					this.addToOffice365Calendar(status.url);
				}
				this.loading.set(false);
			},
			error: (_error: unknown) => {
				this.loading.set(false);
			}
		});
	}

	addToOffice365Calendar(url: string): void {
		// Office 365 (Business/School) works best with https:// for the url parameter
		const officeUrl = `https://outlook.office.com/calendar/0/addfromweb?url=${encodeURIComponent(url)}&name=${encodeURIComponent('LanguageBird Calendar')}`;
		window.open(officeUrl, '_blank');
	}

	disableSync(): void {
		this._dialogService.open(ConfirmationDialogComponent as Type<any>, {
			title: 'Disable Calendar Sync?',
			body: 'Are you sure you want to disable calendar sync? Your calendar link will stop working immediately.',
			successText: 'Disable',
			icon: 'untitled-ui/alerts/alert-triangle',
			action: () => {
				return this._calendarService.revokeToken().pipe(
					tap(() => {
						this.calendarTokenStatus.set(null);
					})
				);
			}
		});
	}
}
